import React, { useEffect, useState } from "react";
import { getUserCoupons } from "../utils/referralUtils";

const MyCoupons = ({ userId }) => {
  const [coupons, setCoupons] = useState({ referralCoupons: [], welcomeCoupons: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCoupons = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const userCoupons = await getUserCoupons(userId);
        setCoupons(userCoupons);
      } catch (error) {
        console.error("Error fetching user coupons:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, [userId]);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600";
      case "used":
        return "text-gray-500";
      case "expired":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case "active":
        return "🟢";
      case "used":
        return "✅";
      case "expired":
        return "❌";
      default:
        return "•";
    }
  };

  const getCouponStatus = (coupon) => {
    const today = new Date();
    const expiryDate = new Date(coupon.expiryDate);
    
    if (coupon.usedCount > 0) return "used";
    if (expiryDate < today) return "expired";
    return "active";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const totalCoupons = coupons.referralCoupons.length + coupons.welcomeCoupons.length;

  if (totalCoupons === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg border border-green-100 mb-6">
      <h2 className="text-lg font-semibold mb-4 text-green-600 flex items-center">
        🎁 My Coupons ({totalCoupons})
      </h2>

      {/* Referral Coupons */}
      {coupons.referralCoupons.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-700 mb-3 flex items-center">
            🎯 Referral Rewards ({coupons.referralCoupons.length}/5)
          </h3>
          <div className="space-y-3">
            {coupons.referralCoupons.map((coupon) => {
              const status = getCouponStatus(coupon);
              return (
                <div key={coupon.id} className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold text-green-700 text-sm">
                          {coupon.code}
                        </span>
                        <span className={`text-xs ${getStatusColor(status)}`}>
                          {getStatusEmoji(status)} {status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        {coupon.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        Referred: {coupon.referredUserEmail || "Unknown"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Expires: {formatDate(coupon.expiryDate)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-700">₹150 OFF</div>
                      <div className="text-xs text-gray-500">
                        Max discount
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Welcome Coupons */}
      {coupons.welcomeCoupons.length > 0 && (
        <div>
          <h3 className="font-medium text-gray-700 mb-3 flex items-center">
            🎉 Welcome Offers ({coupons.welcomeCoupons.length})
          </h3>
          <div className="space-y-3">
            {coupons.welcomeCoupons.map((coupon) => {
              const status = getCouponStatus(coupon);
              return (
                <div key={coupon.id} className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold text-blue-700 text-sm">
                          {coupon.code}
                        </span>
                        <span className={`text-xs ${getStatusColor(status)}`}>
                          {getStatusEmoji(status)} {status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        {coupon.description}
                      </div>
                      <div className="text-xs text-gray-500">
                        Expires: {formatDate(coupon.expiryDate)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-700">₹100 OFF</div>
                      <div className="text-xs text-gray-500">
                        Max discount
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        💡 Use these coupon codes during checkout to get discounts on your subscriptions!
      </div>
    </div>
  );
};

export default MyCoupons; 